import { call, put, takeEvery } from 'redux-saga/effects';
import { get, post, postForm, update, destroy } from 'utils/api';
import * as actions from './UserManagement.actions';
import * as types from './UserManagement.types';

// Fetch users with pagination and search
function* fetchUsersSaga(action) {
  try {
    
    const { 
      page = 0, 
      limit = 10, 
      search = '', 
      searchName = '',
      searchRut = '',
      searchEmail = '',
      userType = '', 
      status = '' 
    } = action.payload || {};
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(searchName && { searchName }),
      ...(searchRut && { searchRut }),
      ...(searchEmail && { searchEmail }),
      ...(userType && { userType }),
      ...(status && { status }),
    }).toString();

    // First try the management endpoint, fallback to regular users endpoint for testing
    let url = `api/v1/users/management?${queryParams}`;

    let response = yield call(get, url);
    
    // If management endpoint has issues, try regular users endpoint
    if (response.error && (
      response.errorMsg.includes('ENOENT') || 
      response.errorMsg.includes('normalizeObj') ||
      response.errorMsg.includes('500')
    )) {
      url = `api/v1/users`;
      response = yield call(get, url);
    }
    
    if (response.error) {
      yield put(actions.fetchUsersError(response.errorMsg || 'Error al obtener usuarios'));
    } else {
      const successPayload = {
        users: response.objects?.users || {},
        pagination: response.pagination || {
          total: 0,
          page: 0,
          limit: 10,
          totalPages: 0
        },
        userOrder: response.order.users || []
      };
      yield put(actions.fetchUsersSuccess(successPayload));
    }
  } catch (error) {
    yield put(actions.fetchUsersError(error.message || 'Error al obtener usuarios'));
  }
}

// Fetch single user details
function* fetchUserDetailsSaga(action) {
  try {
    const userId = action.payload;
    const response = yield call(get, `api/v1/users/${userId}`);
    
    if (response.error) {
      yield put(actions.fetchUserDetailsError(response.errorMsg || 'Error al obtener detalles del usuario'));
    } else {
      const userArray = Object.values(response.objects?.users || {});
      const user = userArray[0];
      yield put(actions.fetchUserDetailsSuccess(user));
    }
  } catch (error) {
    yield put(actions.fetchUserDetailsError(error.message || 'Error al obtener detalles del usuario'));
  }
}

// Reset user password using the original auth endpoint
function* resetPasswordSaga(action) {
  try {
    const userEmail = action.payload; // Now we expect email instead of userId
    const response = yield call(post, 'api/v1/auth/reset-password', { email: userEmail });
    
    if (response.error) {
      yield put(actions.resetPasswordError(response.errorMsg || 'Error al restablecer contraseña'));
    } else {
      yield put(actions.resetPasswordSuccess(response.message || 'Si el correo electrónico pertenece al sistema, recibirá una notificación'));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: response.message || 'Si el correo electrónico pertenece al sistema, recibirá una notificación'
        }
      });
    }
  } catch (error) {
    yield put(actions.resetPasswordError(error.message || 'Error al restablecer contraseña'));
  }
}

// Set user password using the new auth endpoint
function* setPasswordSaga(action) {
  try {
    const { email, password } = action.payload;
    const response = yield call(post, 'api/v1/auth/set-password', { email, password });
    
    if (response.error) {
      yield put(actions.setPasswordError(response.errorMsg || 'Error al configurar contraseña'));
      
      // Show error notification
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: response.errorMsg || 'Error al configurar contraseña'
        }
      });
    } else {
      yield put(actions.setPasswordSuccess(response.message || 'Contraseña configurada exitosamente'));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: response.message || 'Contraseña configurada exitosamente. Se ha enviado un correo al usuario.'
        }
      });
    }
  } catch (error) {
    yield put(actions.setPasswordError(error.message || 'Error al configurar contraseña'));
    
    // Show error notification
    yield put({
      type: 'app/SET_ERROR',
      payload: {
        msg: error.message || 'Error al configurar contraseña'
      }
    });
  }
}

// NEW: Fetch user representatives
function* fetchUserRepresentativesSaga(action) {
  try {
    const userId = action.payload;
    // Add timestamp to avoid cache
    const timestamp = Date.now();
    const response = yield call(get, `api/v1/users/${userId}/representatives?_t=${timestamp}`);
    
    if (response.error) {
      yield put(actions.fetchUserRepresentativesError(response.errorMsg || 'Error al obtener representantes'));
    } else {
      const representatives = Object.values(response.objects?.representatives || {});
      yield put(actions.fetchUserRepresentativesSuccess(representatives));
    }
  } catch (error) {
    yield put(actions.fetchUserRepresentativesError(error.message || 'Error al obtener representantes'));
  }
}

// NEW: Fetch user powers
function* fetchUserPowersSaga(action) {
  try {
    const userId = action.payload;
    const response = yield call(get, `api/v1/users/${userId}/powers`);
    
    if (response.error) {
      yield put(actions.fetchUserPowersError(response.errorMsg || 'Error al obtener poderes'));
    } else {
      const powers = Object.values(response.objects?.powers || {});
      yield put(actions.fetchUserPowersSuccess(powers));
    }
  } catch (error) {
    yield put(actions.fetchUserPowersError(error.message || 'Error al obtener poderes'));
  }
}

// Legacy sagas (keeping for future use)
function* createUserSaga(action) {
  try {
    const response = yield call(post, 'api/v1/users', action.payload);
    
    if (response.error) {
      yield put(actions.createUserError(response.errorMsg || 'Error al crear usuario'));
    } else {
      yield put(actions.createUserSuccess(response));
    }
  } catch (error) {
    yield put(actions.createUserError(error.message || 'Error al crear usuario'));
  }
}

function* updateUserSaga(action) {
  try {
    const { userId, userData } = action.payload;
    const response = yield call(update, `api/v1/users/${userId}`, userData);
    
    if (response.error) {
      yield put(actions.updateUserError(response.errorMsg || 'Error al actualizar usuario'));
      
      // Show error notification
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: response.errorMsg || 'Error al actualizar usuario'
        }
      });
    } else {
      yield put(actions.updateUserSuccess(response));

      // Refresh user list first
      yield put(actions.fetchUsers({
        page: 0,
        limit: 10
      }));
      
      // Show success notification after refresh
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: response.message || 'Usuario actualizado exitosamente'
        }
      });
    }
  } catch (error) {
    yield put(actions.updateUserError(error.message || 'Error al actualizar usuario'));
    
    // Show error notification for exceptions
    yield put({
      type: 'app/SET_ERROR',
      payload: {
        msg: error.message || 'Error al actualizar usuario'
      }
    });
  }
}

function* deleteUserSaga(action) {
  try {
    const userId = action.payload;
    const response = yield call(destroy, `api/v1/users/${userId}`);
    
    if (response.error) {
      yield put(actions.deleteUserError(response.errorMsg || 'Error al eliminar usuario'));
    } else {
      yield put(actions.deleteUserSuccess(userId));
    }
  } catch (error) {
    yield put(actions.deleteUserError(error.message || 'Error al eliminar usuario'));
  }
}

// NEW: Update user representatives
function* updateUserRepresentativesSaga(action) {
  try {
    const { userId, representativesArray } = action.payload;
    
    const response = yield call(update, `api/v1/users/${userId}/representatives`, {
      representativesArray
    });
    
    
    if (response.error) {
      yield put(actions.updateUserRepresentativesError(response.errorMsg || 'Error al actualizar representantes'));
    } else {
      yield put(actions.updateUserRepresentativesSuccess(response.message || 'Representantes actualizados exitosamente'));
      
      // Refresh representatives list - add timestamp to avoid cache
      yield put(actions.fetchUserRepresentatives(userId));
      
      // Also refresh the search results to reflect the changes
      yield put(actions.searchNaturalEntities(userId, { search: '', page: 0, limit: 20 }));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: response.message || 'Representantes actualizados exitosamente'
        }
      });
    }
  } catch (error) {
    yield put(actions.updateUserRepresentativesError(error.message || 'Error al actualizar representantes'));
  }
}

// NEW: Search natural entities for representatives
function* searchNaturalEntitiesSaga(action) {
  try {
    const { legalUserId, params } = action.payload;
    const { search = '', page = 0, limit = 10 } = params;
    
    const queryParams = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      _t: Date.now().toString() // Add timestamp to avoid cache
    }).toString();

    const response = yield call(get, `api/v1/users/${legalUserId}/search-natural-entities?${queryParams}`);
    
    if (response.error) {
      yield put(actions.searchNaturalEntitiesError(response.errorMsg || 'Error al buscar entidades naturales'));
    } else {
      const entities = Object.values(response.objects?.entities || {});
      yield put(actions.searchNaturalEntitiesSuccess({
        entities,
        pagination: response.pagination || {
          total: 0,
          page: 0,
          limit: 10,
          totalPages: 0
        }
      }));
    }
  } catch (error) {
    yield put(actions.searchNaturalEntitiesError(error.message || 'Error al buscar entidades naturales'));
  }
}

// NEW: Create user power (legal entity file)
function* createUserPowerSaga(action) {
  try {
    const { userId, powerData } = action.payload;
    const { legalPowerFile } = powerData;


    // Validate file exists
    if (!legalPowerFile) {
      yield put(actions.createUserPowerError('No se proporcionó ningún archivo'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: 'No se proporcionó ningún archivo'
        }
      });
      return;
    }

    // Validate file type (allow various PDF MIME types)
    const allowedTypes = ['application/pdf', 'application/x-pdf', 'application/acrobat', 'application/vnd.pdf'];
    if (!allowedTypes.includes(legalPowerFile.type) && !legalPowerFile.name.toLowerCase().endsWith('.pdf')) {
      yield put(actions.createUserPowerError('Los archivos de poder deben ser archivos PDF'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: 'Los archivos de poder deben ser archivos PDF'
        }
      });
      return;
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', legalPowerFile, legalPowerFile.name);

    // Use postForm with FormData
    const response = yield call(postForm, `api/v1/legalEntities/${userId}`, formData);

    if (response.error) {
      yield put(actions.createUserPowerError(response.errorMsg || 'Error al crear poder'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: response.errorMsg || 'Error al crear poder'
        }
      });
    } else {
      yield put(actions.createUserPowerSuccess(response));
      
      // Refresh powers list
      yield put(actions.fetchUserPowers(userId));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: 'Poder agregado exitosamente'
        }
      });
    }
  } catch (error) {
    yield put(actions.createUserPowerError(error.message || 'Error al crear poder'));
  }
}

// NEW: Delete user power (legal entity file)
function* deleteUserPowerSaga(action) {
  try {
    const { userId, powerId } = action.payload;


    const response = yield call(destroy, `api/v1/legalEntities/${powerId}`);

    if (response.error) {
      yield put(actions.deleteUserPowerError(response.errorMsg || 'Error al eliminar poder'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: response.errorMsg || 'Error al eliminar poder'
        }
      });
    } else {
      yield put(actions.deleteUserPowerSuccess(powerId));
      
      // Refresh powers list
      yield put(actions.fetchUserPowers(userId));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: 'Poder eliminado exitosamente'
        }
      });
    }
  } catch (error) {
    yield put(actions.deleteUserPowerError(error.message || 'Error al eliminar poder'));
  }
}

// NEW: Replace user power (delete + create in sequence)
function* replaceUserPowerSaga(action) {
  try {
    const { userId, powerId, powerData } = action.payload;
    const { legalPowerFile } = powerData;


    // Validate file exists
    if (!legalPowerFile) {
      yield put(actions.replaceUserPowerError('No se proporcionó ningún archivo'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: 'No se proporcionó ningún archivo'
        }
      });
      return;
    }

    // Validate file type (allow various PDF MIME types)
    const allowedTypes = ['application/pdf', 'application/x-pdf', 'application/acrobat', 'application/vnd.pdf'];
    if (!allowedTypes.includes(legalPowerFile.type) && !legalPowerFile.name.toLowerCase().endsWith('.pdf')) {
      yield put(actions.replaceUserPowerError('Los archivos de poder deben ser archivos PDF'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: 'Los archivos de poder deben ser archivos PDF'
        }
      });
      return;
    }

    // Step 1: Delete the existing power
    const deleteResponse = yield call(destroy, `api/v1/legalEntities/${powerId}`);

    if (deleteResponse.error) {
      yield put(actions.replaceUserPowerError(deleteResponse.errorMsg || 'Error al eliminar poder existente'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: deleteResponse.errorMsg || 'Error al eliminar poder existente'
        }
      });
      return;
    }


    // Step 2: Create the new power
    const formData = new FormData();
    formData.append('file', legalPowerFile, legalPowerFile.name);

    const createResponse = yield call(postForm, `api/v1/legalEntities/${userId}`, formData);

    if (createResponse.error) {
      yield put(actions.replaceUserPowerError(createResponse.errorMsg || 'Error al crear nuevo poder'));
      yield put({
        type: 'app/SET_ERROR',
        payload: {
          msg: createResponse.errorMsg || 'Error al crear nuevo poder'
        }
      });
    } else {
      yield put(actions.replaceUserPowerSuccess(createResponse));
      
      // Refresh powers list
      yield put(actions.fetchUserPowers(userId));
      
      // Show success notification
      yield put({
        type: 'app/SET_SUCCESS',
        payload: {
          msg: 'Poder reemplazado exitosamente'
        }
      });
    }
  } catch (error) {
    yield put(actions.replaceUserPowerError(error.message || 'Error al reemplazar poder'));
  }
}

export default function* userManagementSaga() {
  yield takeEvery(types.USER_MANAGEMENT_FETCH_USERS, fetchUsersSaga);
  yield takeEvery(types.USER_MANAGEMENT_FETCH_USER_DETAILS, fetchUserDetailsSaga);
  yield takeEvery(types.USER_MANAGEMENT_FETCH_USER_REPRESENTATIVES, fetchUserRepresentativesSaga);
  yield takeEvery(types.USER_MANAGEMENT_FETCH_USER_POWERS, fetchUserPowersSaga);
  yield takeEvery(types.USER_MANAGEMENT_RESET_PASSWORD, resetPasswordSaga);
  yield takeEvery(types.USER_MANAGEMENT_SET_PASSWORD, setPasswordSaga);
  yield takeEvery(types.USER_MANAGEMENT_CREATE_USER, createUserSaga);
  yield takeEvery(types.USER_MANAGEMENT_UPDATE_USER, updateUserSaga);
  yield takeEvery(types.USER_MANAGEMENT_DELETE_USER, deleteUserSaga);
  yield takeEvery(types.USER_MANAGEMENT_UPDATE_USER_REPRESENTATIVES, updateUserRepresentativesSaga);
  yield takeEvery(types.USER_MANAGEMENT_SEARCH_NATURAL_ENTITIES, searchNaturalEntitiesSaga);
  yield takeEvery(types.USER_MANAGEMENT_CREATE_USER_POWER, createUserPowerSaga);
  yield takeEvery(types.USER_MANAGEMENT_DELETE_USER_POWER, deleteUserPowerSaga);
  yield takeEvery(types.USER_MANAGEMENT_REPLACE_USER_POWER, replaceUserPowerSaga);
}